import { Effect, Data, Context, Queue, Scope, Layer, PubSub } from "effect";

export interface AddToCartEventsImpl {
  readonly publish: (event: AddToCartEvent) => Effect.Effect<void>;
  /**
   * Subscribe returns a Dequeue that is only valid within a Scope.
   * (This is why it returns an Effect requiring Scope.)
   */
  readonly subscribe: Effect.Effect<
    Queue.Dequeue<AddToCartEvent>,
    never,
    Scope.Scope
  >;
}

export type AddToCartEvent =
  | {
    _tag: "before";
    requestId: string;
    data: unknown;
    submitter: HTMLElement | null;
    form: HTMLFormElement | null;
  }
  | {
    _tag: "success";
    requestId: string;
    data: unknown;
    result: unknown;
    submitter: HTMLElement | null;
    form: HTMLFormElement | null;
  }
  | {
    _tag: "error";
    requestId: string;
    data: unknown;
    error: unknown;
    submitter: HTMLElement | null;
    form: HTMLFormElement | null;
  }
  | {
    _tag: "finally";
    requestId: string;
    data: unknown;
    submitter: HTMLElement | null;
    form: HTMLFormElement | null;
  };

export class AddToCartEvents extends Context.Tag("cart/AddToCartEvents")<
  AddToCartEvents,
  AddToCartEventsImpl
>() {
  /**
   * Live Layer: creates the PubSub once and exposes publish/subscribe.
   *
   * Use sliding / dropping / bounded in the browser to avoid unbounded memory growth.
   * Unbounded pubsubs can grow indefinitely if producers outpace consumers. :contentReference[oaicite:6]{index=6}
   */
  static Live = Layer.effect(
    this,
    Effect.gen(function* () {
      // Sliding avoids backpressure and bounds memory. :contentReference[oaicite:7]{index=7}
      const pubsub = yield* PubSub.sliding<AddToCartEvent>(64);

      const publish = (event: AddToCartEvent) =>
        PubSub.publish(pubsub, event).pipe(
          // PubSub.publish returns boolean; we just ignore it here. :contentReference[oaicite:8]{index=8}
          Effect.asVoid,
        );

      const subscribe = PubSub.subscribe(pubsub);
      // subscribe: Effect<Queue.Dequeue<A>, never, Scope> :contentReference[oaicite:9]{index=9}

      return { publish, subscribe } satisfies AddToCartEventsImpl;
    }),
  );
}
